import React from "react";
import { useDispatch } from "react-redux";
import { Dispatch, SetStateAction } from "react";
import { Button, Typography, Dialog }  from "@material-tailwind/react";
import { FormProvider, useForm } from 'react-hook-form'

import { User } from "iconoir-react";
import FormInput from "../../../components/FormInput";
import { name_validation, date_validation, year_validation } from "../../../utils/inputValidations";
import FormFileUpload from "../../../components/FormFileUpload";
import { Employee } from "../../../redux/employee/actions";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux";
import { rolesRequest, Team } from "../../../redux/auth/actions";
import { FormTagInput } from "../../../components/FormTagInput";
import { Store } from "../../../redux/vehicles/actions";

const DETALIS_FIELDS = [
    { n: "Name", f: "user.fullname", type: "text" },
    { n: "Email", f: "user.email", type: "text" },
    { n: "Password", f: "password", type: "text" },
    { n: "Role", f: "role_id", type: "select", options: []},
    { n: "Team", f: "teams", type: "tags", options: []},
    { n: "Location", f: "stores", type: "tags", options: []},
    { n: "Country of Citizenship", f: "country_of_citizenship", type: "text" },
    { n: "Weekly Payout Limit", f: "weekly_cap", type: "text" },
    { n: "Work hours limit", f: "work_hours_limit", type: "text" },
    { n: "Hour Rate", f: "hour_rate", type: "text" },
    { n: "Contract Type", f: "contract_type", type: "select", options: [{value: 'Hourly', label: 'Hourly'}, {value: 'Salary', label: 'Salary'}] },
    { n: "Current Payout Balance", f: "current_payout_balance", type: "text" },
];

interface DialogAddEmployeeProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    onSubmit: (employee: Employee) => void;
}

export const DialogAddEmployee: React.FC<DialogAddEmployeeProps> = ({
    isOpen,
    setIsOpen,
    onSubmit,
}) => {
    const methods = useForm();
    const dispatch = useDispatch();
    const { stores } = useSelector((state: RootState) => state.vehicles);
    const { roles, teams } = useSelector((state: RootState) => state.auth);

    React.useEffect(() => {
        dispatch(rolesRequest());
    }, [dispatch]);

    React.useEffect(() => {
        if (stores) {
            DETALIS_FIELDS.forEach((field) => {
                if (field.f === "stores" && field.options?.length === 0) {
                    field.options = stores.map((store: Store) => ({
                        value: store.id.toString(),
                        label: `${store.title} ${store.location}`,
                    }));
                }
            });
        }
    }, [stores]);

    React.useEffect(() => {
        if (teams) {
            DETALIS_FIELDS.forEach((field) => {
                if (field.f === "teams" && field.options?.length === 0) {
                    field.options = teams.map((team: Team) => ({
                        value: team.id.toString(),
                        label: team.title,
                    }));
                }
            });
        }
    }, [teams]);

    React.useEffect(() => {
        if (roles) {
            DETALIS_FIELDS.forEach(field => {
                if (field.f === "role_id") {
                    field.options = roles.map(role => ({
                        value: role.id.toString(),
                        label: role.title
                    }));
                }
            });
        }
    }, [roles]);

    const onFormSubmit = methods.handleSubmit(data => {
        onSubmit(
            {
                ...data,
                avatar: data.avatar?.length ? data.avatar[0] : null,
                role_id: data.role_id.value,
                stores: data.stores.map((store: any) => store.value),
                teams: data.teams.map((team: any) => team.value),
                contract_type: data.contract_type.value
            } as Employee
        );
    });

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen} size="lg">
            <Dialog.Overlay>
                <Dialog.Content className="p-2 md:p-8">
                    <div className="flex flex-col gap-1 p-2 md:p-6 pb-4 border-b border-surface">
                        <Typography type="h5" color="default">
                            New Employee
                        </Typography>
                    </div>
                    <FormProvider {...methods}>
                        <form
                            className="flex flex-col p-2 md:p-6"
                            onSubmit={(e) => e.preventDefault()}
                            noValidate
                            autoComplete="off"
                        >
                            <div className="flex flex-col gap-1 pb-6 border-b border-surface">
                                <FormFileUpload
                                    show_preview={true}
                                    title=""
                                    desc=""
                                    accept="image/*"
                                    name="avatar"
                                    btnClass="!h-[40px]"
                                    Icon={User}
                                    iconClass="!w-8 !h-8 text-primary"
                                />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 justify-stretch">
                                {DETALIS_FIELDS.map((field, idx) => (
                                    <div className="" key={idx}>
                                        {field.type === "date" ? (
                                            <FormInput
                                                {...date_validation}
                                                label={field.n}
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                wrapperClass="mt-1 w-[95%]"
                                                name={field.f}
                                                type="text"
                                                placeholder=""
                                            />
                                        ) : field.type === "select" ? (
                                            <FormTagInput
                                                id={field.f}
                                                name={field.f}
                                                label={field.n}
                                                wrapperClass="mt-1 w-[95%]"
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                placeholder=""
                                                options={field?.options || []}
                                            />
                                        ) : field.type === "tags" ? (
                                            <FormTagInput
                                                id={field.f}
                                                name={field.f}
                                                label={field.n}
                                                isMulti={true}
                                                wrapperClass="mt-1 w-[95%]"
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                placeholder=""
                                                options={field?.options || []}
                                            />
                                        ) : field.type === "year" ? (
                                            <FormInput
                                                {...year_validation}
                                                label={field.n}
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                wrapperClass="mt-1 w-[95%]"
                                                name={field.f}
                                                type="number"
                                                placeholder=""
                                            />
                                        ) : (
                                            <FormInput
                                                {...name_validation}
                                                label={field.n}
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                wrapperClass="mt-1 w-[95%]"
                                                name={field.f}
                                                type={
                                                    field.type === "money" ? "number" : field.type
                                                }
                                                placeholder=""
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </form>
                    </FormProvider>
                    <div className="flex gap-3 justify-end p-6 border-t border-surface">
                        <Button
                            variant="outline"
                            color="secondary"
                            onClick={() => setIsOpen(false)}
                            className="mr-1"
                        >
                            Cancel
                        </Button>
                        <Button variant="solid" color="primary" onClick={onFormSubmit}>
                            Confirm
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Overlay>
        </Dialog>
    );
};